import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Menu,
  Tabs,
  Tab,
  Divider,
  LinearProgress,
  Switch,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Tooltip,
  Badge
} from '@mui/material';
import {
  AdminPanelSettings,
  People,
  PersonAdd,
  Search,
  FilterList,
  MoreVert,
  Edit,
  Delete,
  Block,
  CheckCircle,
  Download,
  Upload,
  Refresh,
  Dashboard as DashboardIcon,
  Security,
  Assignment,
  TrendingUp,
  Warning,
  Info,
  Error,
  Settings,
  Close,
  Group,
  Verified,
  MonetizationOn,
  Schedule,
  Analytics,
  Computer,
  Storage,
  CloudQueue
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/adminAPI';
import type { 
  AdminUser, 
  UserRole, 
  SubscriptionPlan, 
  SystemStats,
  UserCreateRequest,
  UserUpdateRequest
} from '../services/adminAPI';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Data
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 0,
    limit: 10,
    pages: 0
  });

  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  
  // Dialogs
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  
  // Role and Plan Dialogs
  const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
  const [createPlanDialogOpen, setCreatePlanDialogOpen] = useState(false);
  const [editPlanDialogOpen, setEditPlanDialogOpen] = useState(false);
  const [deletePlanDialogOpen, setDeletePlanDialogOpen] = useState(false);
  
  // Current selected items for editing
  const [currentRole, setCurrentRole] = useState<UserRole | null>(null);
  const [currentPlan, setCurrentPlan] = useState<SubscriptionPlan | null>(null);
  
  // Form State
  const [formData, setFormData] = useState<UserCreateRequest>({
    email: '',
    password: '',
    full_name: '',
    is_active: true,
    is_superuser: false,
    roles: []
  });

  // Role Form Data
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    description: '',
    permissions: [] as string[]
  });

  // Plan Form Data
  const [planFormData, setPlanFormData] = useState<{
    name: string;
    price: number;
    currency: string;
    interval: string;
    daily_post_limit: number;
    features: string[];
    is_active: boolean;
  }>({
    name: '',
    price: 0,
    currency: 'USD',
    interval: 'monthly',
    daily_post_limit: 10,
    features: [],
    is_active: true
  });

  // Helper function to extract error message from complex error objects
  const extractErrorMessage = (err: any): string => {
    // If it's already a string, return it
    if (typeof err === 'string') return err;
    
    // Try to extract from response.data.detail first
    if (err.response?.data?.detail) {
      const detail = err.response.data.detail;
      // If detail is a string, return it
      if (typeof detail === 'string') return detail;
      // If detail is an array of validation errors, format them
      if (Array.isArray(detail)) {
        return detail.map((error: any) => {
          if (typeof error === 'string') return error;
          if (error.msg) return `${error.loc ? error.loc.join('.') + ': ' : ''}${error.msg}`;
          return JSON.stringify(error);
        }).join('; ');
      }
      // If detail is an object, try to extract message
      if (typeof detail === 'object' && detail.msg) {
        return detail.msg;
      }
    }
    
    // Try response.data.message
    if (err.response?.data?.message) {
      return err.response.data.message;
    }
    
    // Try err.message
    if (err.message) {
      return err.message;
    }
    
    // Fallback
    return 'An unexpected error occurred';
  };

  useEffect(() => {
    fetchData();
  }, [tabValue, pagination.page, pagination.limit, searchTerm, statusFilter, roleFilter]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Create fallback data structure
      const fallbackStats: SystemStats = {
        total_users: 0,
        active_users: 0,
        total_posts: 0,
        posts_today: 0,
        total_ai_generations: 0,
        ai_generations_today: 0,
        subscription_breakdown: {},
        recent_registrations: []
      };

      // Try to fetch admin data, but don't crash if it fails
      try {
        const dashboardData = await adminAPI.getSystemStats();
        setSystemStats(dashboardData?.data || fallbackStats);
      } catch (err) {
        console.warn('Admin stats API not available, using fallback data');
        setSystemStats(fallbackStats);
      }

      try {
        const usersData = await adminAPI.getAllUsers({ 
          page: pagination.page + 1, 
          limit: pagination.limit,
          search: searchTerm || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          role: roleFilter !== 'all' ? roleFilter : undefined
        });
        
        // Defensive checks for nested properties
        const users = usersData?.data?.users || [];
        const paginationData = usersData?.data?.pagination || {};
        
        setUsers(users);
        setPagination(prev => ({ 
          ...prev, 
          total: paginationData.total || 0,
          pages: paginationData.pages || 0
        }));
      } catch (err) {
        console.warn('Admin users API not available');
        setUsers([]);
        // Reset pagination on error
        setPagination(prev => ({ ...prev, total: 0, pages: 0 }));
      }

      // Fetch roles data
      try {
        const rolesData = await adminAPI.getAllRoles();
        setRoles(rolesData?.data || []);
      } catch (err) {
        console.warn('Admin roles API not available');
        setRoles([]);
      }

      // Fetch plans data
      try {
        const plansData = await adminAPI.getAllPlans();
        setPlans(plansData?.data || []);
      } catch (err) {
        console.warn('Admin plans API not available');
        setPlans([]);
      }

    } catch (err: any) {
      console.error('Failed to fetch admin data:', err);
      setError(extractErrorMessage(err));
      // Set fallback data even on error
      setSystemStats({
        total_users: 0,
        active_users: 0,
        total_posts: 0,
        posts_today: 0,
        total_ai_generations: 0,
        ai_generations_today: 0,
        subscription_breakdown: {},
        recent_registrations: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      await adminAPI.createUser(formData);
      setCreateUserDialogOpen(false);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        is_active: true,
        is_superuser: false,
        roles: []
      });
      fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const handleUpdateUser = async () => {
    if (!currentUser) return;
    
    try {
      await adminAPI.updateUser(currentUser.id, {
        email: formData.email,
        full_name: formData.full_name,
        is_active: formData.is_active,
        is_superuser: formData.is_superuser,
        roles: formData.roles
      });
      setEditUserDialogOpen(false);
      setCurrentUser(null);
      fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const handleDeleteUser = async () => {
    if (!currentUser) return;
    
    try {
      await adminAPI.deleteUser(currentUser.id);
      setDeleteDialogOpen(false);
      setCurrentUser(null);
      fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const handleToggleUserStatus = async (user: AdminUser) => {
    try {
      if (user.is_active) {
        await adminAPI.deactivateUser(user.id);
      } else {
        await adminAPI.activateUser(user.id);
      }
      fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedUsers.length === 0) return;
    
    try {
      await adminAPI.bulkUpdateUsers({
        user_ids: selectedUsers,
        action: action as any
      });
      setBulkActionDialogOpen(false);
      setSelectedUsers([]);
      fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: AdminUser) => {
    setAnchorEl(event.currentTarget);
    setCurrentUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentUser(null);
  };

  const handleEditClick = () => {
    if (currentUser) {
      setFormData({
        email: currentUser.email,
        password: '',
        full_name: currentUser.full_name,
        is_active: currentUser.is_active,
        is_superuser: currentUser.is_superuser,
        roles: currentUser.roles
      });
      setEditUserDialogOpen(true);
    }
    handleMenuClose();
  };

  // Role Management Handlers
  const handleCreateRole = async () => {
    try {
      await adminAPI.createRole(roleFormData);
      setCreateRoleDialogOpen(false);
      setRoleFormData({ name: '', description: '', permissions: [] });
      fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const handleUpdateRole = async () => {
    if (!currentRole) return;
    try {
      await adminAPI.updateRole(currentRole.id, roleFormData);
      setEditRoleDialogOpen(false);
      setCurrentRole(null);
      fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const handleDeleteRole = async () => {
    if (!currentRole) return;
    try {
      await adminAPI.deleteRole(currentRole.id);
      setDeleteRoleDialogOpen(false);
      setCurrentRole(null);
      fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  // Plan Management Handlers
  const handleCreatePlan = async () => {
    try {
      // Convert features array to object format expected by backend
      const planDataForBackend = {
        ...planFormData,
        features: planFormData.features.reduce((acc: Record<string, boolean>, feature: string) => {
          // Convert feature name to key format and set to true
          const key = feature.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
          acc[key] = true;
          return acc;
        }, {})
      };
      
      await adminAPI.createPlan(planDataForBackend);
      setCreatePlanDialogOpen(false);
      setPlanFormData({
        name: '',
        price: 0,
        currency: 'USD',
        interval: 'monthly',
        daily_post_limit: 10,
        features: [],
        is_active: true
      });
      fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const handleUpdatePlan = async () => {
    if (!currentPlan) return;
    try {
      // Convert features array to object format expected by backend
      const planDataForBackend = {
        ...planFormData,
        features: planFormData.features.reduce((acc: Record<string, boolean>, feature: string) => {
          // Convert feature name to key format and set to true
          const key = feature.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
          acc[key] = true;
          return acc;
        }, {})
      };
      
      await adminAPI.updatePlan(currentPlan.id, planDataForBackend);
      setEditPlanDialogOpen(false);
      setCurrentPlan(null);
      fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const handleDeletePlan = async () => {
    if (!currentPlan) return;
    try {
      await adminAPI.deletePlan(currentPlan.id);
      setDeletePlanDialogOpen(false);
      setCurrentPlan(null);
      fetchData();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const renderDashboardTab = () => (
    <Grid container spacing={3}>
      {/* Stats Cards */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="div" color="primary" fontWeight="bold">
                  {formatNumber(systemStats?.total_users || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Users
                </Typography>
                <Chip 
                  label={`${systemStats?.active_users || 0} Active`} 
                  color="success" 
                  size="small" 
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="div" color="success.main" fontWeight="bold">
                  {formatNumber(systemStats?.total_posts || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Posts
                </Typography>
                <Chip 
                  label={`${systemStats?.posts_today || 0} Today`} 
                  color="info" 
                  size="small" 
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="div" color="info.main" fontWeight="bold">
                  {formatNumber(systemStats?.total_ai_generations || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  AI Generations
                </Typography>
                <Chip 
                  label={`${systemStats?.ai_generations_today || 0} Today`} 
                  color="warning" 
                  size="small" 
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="div" color="warning.main" fontWeight="bold">
                  {systemStats?.subscription_breakdown ? Object.keys(systemStats.subscription_breakdown).length : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Plans
                </Typography>
                <Chip 
                  label="Revenue Tracking" 
                  color="success" 
                  size="small" 
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      {/* Subscription Breakdown */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <MonetizationOn sx={{ mr: 1 }} />
            Subscription Breakdown
          </Typography>
          {systemStats?.subscription_breakdown ? (
            Object.entries(systemStats.subscription_breakdown).map(([plan, count]) => (
              <Box key={plan} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">{plan}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {count} users
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(count / (systemStats.total_users || 1)) * 100} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No subscription data available
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* Recent Registrations */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonAdd sx={{ mr: 1 }} />
            Recent Registrations
          </Typography>
          {systemStats?.recent_registrations && systemStats.recent_registrations.length > 0 ? (
            systemStats.recent_registrations.map((user) => (
              <Card key={user.id} sx={{ mb: 1 }}>
                <CardContent sx={{ py: 1 }}>
                  <Typography variant="subtitle2">{user.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(user.created_at).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No recent registrations
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );

  const renderUsersTab = () => (
    <Box>
      {/* Users Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">User Management</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedUsers.length > 0 && (
            <Button
              startIcon={<Group />}
              onClick={() => setBulkActionDialogOpen(true)}
              color="warning"
            >
              Bulk Actions ({selectedUsers.length})
            </Button>
          )}
          <Button
            startIcon={<PersonAdd />}
            onClick={() => setCreateUserDialogOpen(true)}
            variant="contained"
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as any)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl size="small" fullWidth disabled>
              <InputLabel>Role (Not Available)</InputLabel>
              <Select
                value="all"
                label="Role (Not Available)"
                disabled
              >
                <MenuItem value="all">All Roles</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              startIcon={<Refresh />}
              onClick={fetchData}
              disabled={loading}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                  checked={users.length > 0 && selectedUsers.length === users.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedUsers(users.map(user => user.id));
                    } else {
                      setSelectedUsers([]);
                    }
                  }}
                />
              </TableCell>
              <TableCell>User</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Subscription</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedUsers.includes(user.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers(prev => [...prev, user.id]);
                      } else {
                        setSelectedUsers(prev => prev.filter(id => id !== user.id));
                      }
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                      {user.full_name || user.email}
                      {user.is_superuser && (
                        <Verified color="primary" sx={{ ml: 1, fontSize: 16 }} />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {user.roles.map((role) => (
                      <Chip 
                        key={role} 
                        label={role} 
                        size="small" 
                        color={role === 'admin' ? 'error' : 'default'}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={user.subscription?.plan || 'Free'} 
                    size="small"
                    color={user.subscription?.plan === 'enterprise' ? 'success' : 
                           user.subscription?.plan === 'pro' ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {user.daily_posts_used || 0}/{user.daily_posts_limit || 10} posts
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={((user.daily_posts_used || 0) / (user.daily_posts_limit || 10)) * 100}
                    sx={{ mt: 0.5 }}
                  />
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={user.is_active}
                        onChange={() => handleToggleUserStatus(user)}
                        size="small"
                      />
                    }
                    label={user.is_active ? 'Active' : 'Inactive'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={(e) => handleMenuClick(e, user)}>
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={(_, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={(e) => {
            setPagination(prev => ({ 
              ...prev, 
              limit: parseInt(e.target.value, 10),
              page: 0
            }));
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>
    </Box>
  );

  const renderRolesTab = () => (
    <Box>
      {/* Roles Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Role Management</Typography>
        <Button
          startIcon={<PersonAdd />}
          onClick={() => setCreateRoleDialogOpen(true)}
          variant="contained"
        >
          Add Role
        </Button>
      </Box>

      {/* Roles Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id} hover>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {role.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {role.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {Array.isArray(role.permissions) && role.permissions.slice(0, 3).map((permission, index) => (
                      <Chip 
                        key={index} 
                        label={permission} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                    {Array.isArray(role.permissions) && role.permissions.length > 3 && (
                      <Chip 
                        label={`+${role.permissions.length - 3} more`} 
                        size="small" 
                        color="default"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(role.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    onClick={() => {
                      setCurrentRole(role);
                      setRoleFormData({
                        name: role.name,
                        description: role.description,
                        permissions: Array.isArray(role.permissions) ? role.permissions : []
                      });
                      setEditRoleDialogOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton 
                    onClick={() => {
                      setCurrentRole(role);
                      setDeleteRoleDialogOpen(true);
                    }}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {roles.length === 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Security sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Roles Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create your first role to get started with role-based access control.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  const renderPlansTab = () => (
    <Box>
      {/* Plans Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Subscription Plans</Typography>
        <Button
          startIcon={<MonetizationOn />}
          onClick={() => setCreatePlanDialogOpen(true)}
          variant="contained"
        >
          Add Plan
        </Button>
      </Box>

      {/* Plans Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Plan Name</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Interval</TableCell>
              <TableCell>Daily Posts</TableCell>
              <TableCell>Features</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((plan) => (
              <TableRow key={plan.id} hover>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {plan.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="h6" color="primary">
                    {plan.currency} {plan.price}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={plan.interval} 
                    size="small" 
                    color="info"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {plan.daily_post_limit} posts/day
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {plan.features && typeof plan.features === 'object' && 
                     Object.keys(plan.features).filter(key => plan.features[key]).slice(0, 2).map((feature, index) => (
                      <Chip 
                        key={index} 
                        label={feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                        size="small" 
                        color="secondary"
                        variant="outlined"
                      />
                    ))}
                    {plan.features && typeof plan.features === 'object' && 
                     Object.keys(plan.features).filter(key => plan.features[key]).length > 2 && (
                      <Chip 
                        label={`+${Object.keys(plan.features).filter(key => plan.features[key]).length - 2} more`} 
                        size="small" 
                        color="default"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={plan.is_active ? 'Active' : 'Inactive'} 
                    size="small" 
                    color={plan.is_active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    onClick={() => {
                      setCurrentPlan(plan);
                      setPlanFormData({
                        name: plan.name,
                        price: plan.price,
                        currency: plan.currency,
                        interval: plan.interval,
                        daily_post_limit: plan.daily_post_limit,
                        features: plan.features && typeof plan.features === 'object' 
                          ? Object.keys(plan.features).filter(key => plan.features[key]).map(key => 
                              key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                            )
                          : [],
                        is_active: plan.is_active
                      });
                      setEditPlanDialogOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton 
                    onClick={() => {
                      setCurrentPlan(plan);
                      setDeletePlanDialogOpen(true);
                    }}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {plans.length === 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <MonetizationOn sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Plans Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create your first subscription plan to get started with monetization.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AdminPanelSettings color="primary" sx={{ mr: 2, fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Admin Dashboard
            </Typography>
          </Box>
          
          <Button
            startIcon={<DashboardIcon />}
            onClick={() => navigate('/dashboard')}
            variant="outlined"
          >
            Back to Dashboard
          </Button>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          System administration and user management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 3 }} />}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Dashboard" icon={<DashboardIcon />} />
          <Tab label="Users" icon={<People />} />
          <Tab label="Roles" icon={<Security />} />
          <Tab label="Plans" icon={<MonetizationOn />} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        {renderDashboardTab()}
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        {renderUsersTab()}
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        {renderRolesTab()}
      </TabPanel>
      
      <TabPanel value={tabValue} index={3}>
        {renderPlansTab()}
      </TabPanel>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditClick}>
          <Edit sx={{ mr: 1 }} />
          Edit User
        </MenuItem>
        <MenuItem onClick={() => { setDeleteDialogOpen(true); handleMenuClose(); }}>
          <Delete sx={{ mr: 1 }} color="error" />
          Delete User
        </MenuItem>
        <MenuItem onClick={() => { 
          if (currentUser) handleToggleUserStatus(currentUser); 
          handleMenuClose(); 
        }}>
          {currentUser?.is_active ? <Block sx={{ mr: 1 }} /> : <CheckCircle sx={{ mr: 1 }} />}
          {currentUser?.is_active ? 'Deactivate' : 'Activate'}
        </MenuItem>
      </Menu>

      {/* Create User Dialog */}
      <Dialog open={createUserDialogOpen} onClose={() => setCreateUserDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="password"
                label="Password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_superuser}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_superuser: e.target.checked }))}
                  />
                }
                label="Super User"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onClose={() => setEditUserDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_superuser}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_superuser: e.target.checked }))}
                  />
                }
                label="Super User"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateUser} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{currentUser?.email}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionDialogOpen} onClose={() => setBulkActionDialogOpen(false)}>
        <DialogTitle>Bulk Actions</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Selected {selectedUsers.length} users. Choose an action:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <Button onClick={() => handleBulkAction('activate')} startIcon={<CheckCircle />}>
              Activate Users
            </Button>
            <Button onClick={() => handleBulkAction('deactivate')} startIcon={<Block />}>
              Deactivate Users
            </Button>
            <Button onClick={() => handleBulkAction('delete')} color="error" startIcon={<Delete />}>
              Delete Users
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Create Role Dialog */}
      <Dialog open={createRoleDialogOpen} onClose={() => setCreateRoleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Role</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Role Name"
                value={roleFormData.name}
                onChange={(e) => setRoleFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={roleFormData.description}
                onChange={(e) => setRoleFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Permissions (comma-separated)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="e.g., admin.users.read, admin.users.create, admin.users.update"
                value={roleFormData.permissions.join(', ')}
                onChange={(e) => setRoleFormData(prev => ({ 
                  ...prev, 
                  permissions: e.target.value.split(',').map(p => p.trim()).filter(p => p) 
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateRoleDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateRole} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={editRoleDialogOpen} onClose={() => setEditRoleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Role</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Role Name"
                value={roleFormData.name}
                onChange={(e) => setRoleFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={roleFormData.description}
                onChange={(e) => setRoleFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Permissions (comma-separated)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="e.g., admin.users.read, admin.users.create, admin.users.update"
                value={roleFormData.permissions.join(', ')}
                onChange={(e) => setRoleFormData(prev => ({ 
                  ...prev, 
                  permissions: e.target.value.split(',').map(p => p.trim()).filter(p => p) 
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRoleDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateRole} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Role Dialog */}
      <Dialog open={deleteRoleDialogOpen} onClose={() => setDeleteRoleDialogOpen(false)}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete role "{currentRole?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRoleDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteRole} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Create Plan Dialog */}
      <Dialog open={createPlanDialogOpen} onClose={() => setCreatePlanDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Plan</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Plan Name"
                value={planFormData.name}
                onChange={(e) => setPlanFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Price"
                value={planFormData.price}
                onChange={(e) => setPlanFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={planFormData.currency}
                  label="Currency"
                  onChange={(e) => setPlanFormData(prev => ({ ...prev, currency: e.target.value }))}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="INR">INR</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Billing Interval</InputLabel>
                <Select
                  value={planFormData.interval}
                  label="Billing Interval"
                  onChange={(e) => setPlanFormData(prev => ({ ...prev, interval: e.target.value }))}
                >
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Daily Post Limit"
                value={planFormData.daily_post_limit}
                onChange={(e) => setPlanFormData(prev => ({ ...prev, daily_post_limit: parseInt(e.target.value) || 0 }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={planFormData.is_active}
                    onChange={(e) => setPlanFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Features (comma-separated)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="e.g., AI Content Generation, Image Search, Analytics"
                value={planFormData.features.join(', ')}
                onChange={(e) => setPlanFormData(prev => ({ 
                  ...prev, 
                  features: e.target.value.split(',').map(f => f.trim()).filter(f => f) 
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatePlanDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreatePlan} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog open={editPlanDialogOpen} onClose={() => setEditPlanDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Plan</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Plan Name"
                value={planFormData.name}
                onChange={(e) => setPlanFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Price"
                value={planFormData.price}
                onChange={(e) => setPlanFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={planFormData.currency}
                  label="Currency"
                  onChange={(e) => setPlanFormData(prev => ({ ...prev, currency: e.target.value }))}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="INR">INR</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Billing Interval</InputLabel>
                <Select
                  value={planFormData.interval}
                  label="Billing Interval"
                  onChange={(e) => setPlanFormData(prev => ({ ...prev, interval: e.target.value }))}
                >
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Daily Post Limit"
                value={planFormData.daily_post_limit}
                onChange={(e) => setPlanFormData(prev => ({ ...prev, daily_post_limit: parseInt(e.target.value) || 0 }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={planFormData.is_active}
                    onChange={(e) => setPlanFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Features (comma-separated)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="e.g., AI Content Generation, Image Search, Analytics"
                value={planFormData.features.join(', ')}
                onChange={(e) => setPlanFormData(prev => ({ 
                  ...prev, 
                  features: e.target.value.split(',').map(f => f.trim()).filter(f => f) 
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPlanDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdatePlan} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Plan Dialog */}
      <Dialog open={deletePlanDialogOpen} onClose={() => setDeletePlanDialogOpen(false)}>
        <DialogTitle>Delete Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete plan "{currentPlan?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePlanDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePlan} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPage; 